import { useMemo, useState } from "react"
import { Box, Button, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { RuleSetSelectionRow } from "./rule-set-selection-row"

export function RuleSetSelectionDialog({ ok, cancel, ruleSetItems = [], selectedRuleSets = [] }) {
    const [search, setSearch] = useState("")
    const [selectedIds, setSelectedIds] = useState(getInitialSelectedIds)
    const selectedIdSet = useMemo(getSelectedIdSet, [selectedIds])
    const filteredRuleSets = useMemo(getFilteredRuleSets, [ruleSetItems, search])

    return (
        <Box data-cy="rule-set-selection-dialog">
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="tabler:filter-cog" sx={{ fontSize: 24 }} />
                    <Stack spacing={0.5}>
                        <Typography variant="h6">Select Rule Sets</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Choose one or more reusable rule sets to apply to the starting scenario.
                        </Typography>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        data-cy="rule-set-selection-search"
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Search rule sets..."
                        size="small"
                        fullWidth
                    />
                    <Typography variant="caption" color="text.secondary">
                        {formatSelectedCount()}
                    </Typography>
                    <If value={filteredRuleSets.length > 0}>
                        <Stack spacing={1} sx={{ maxHeight: 360, overflow: "auto" }}>
                            <Repeat
                                list={filteredRuleSets}
                                item={
                                    <RuleSetSelectionRow selectedIds={selectedIdSet} onToggle={handleToggleRuleSet} />
                                }
                            />
                        </Stack>
                        <Else>
                            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                                <Iconify
                                    icon="mdi:filter-variant-remove"
                                    sx={{ fontSize: 40, color: "text.disabled" }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    No rule sets found.
                                </Typography>
                            </Stack>
                        </Else>
                    </If>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClear} disabled={selectedIds.length === 0}>
                    Clear
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={cancel}>Cancel</Button>
                <Button data-cy="rule-set-selection-apply" variant="contained" onClick={handleApply}>
                    Apply Rule Sets
                </Button>
            </DialogActions>
        </Box>
    )

    function getInitialSelectedIds() {
        return selectedRuleSets.map(toRuleSetId).filter(Boolean)
    }

    function getSelectedIdSet() {
        return new Set(selectedIds)
    }

    function getFilteredRuleSets() {
        const query = search.trim().toLowerCase()
        if (!query) return ruleSetItems
        return ruleSetItems.filter(matchesSearch)

        function matchesSearch(ruleSet) {
            const text = `${ruleSet.name || ""} ${ruleSet.description || ""}`.toLowerCase()
            return text.includes(query)
        }
    }

    function handleSearchChange(event) {
        setSearch(event.target.value)
    }

    function handleToggleRuleSet(ruleSetId) {
        setSelectedIds((current) => {
            if (current.includes(ruleSetId)) return current.filter(removeRuleSetId)
            return [...current, ruleSetId]
        })

        function removeRuleSetId(id) {
            return id !== ruleSetId
        }
    }

    function handleClear() {
        setSelectedIds([])
    }

    function handleApply() {
        const ruleSetsById = new Map([...ruleSetItems, ...selectedRuleSets].map(toRuleSetEntry))
        ok(selectedIds.map((id) => ruleSetsById.get(id)).filter(Boolean))
    }

    function toRuleSetEntry(ruleSet) {
        return [ruleSet.id, ruleSet]
    }

    function toRuleSetId(ruleSet) {
        return ruleSet.id
    }

    function formatSelectedCount() {
        if (selectedIds.length === 1) return "1 rule set selected"
        return `${selectedIds.length} rule sets selected`
    }
}
